import React, { useEffect, useState } from 'react';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { Feature, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { faArchive } from '@fortawesome/pro-solid-svg-icons/faArchive';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { facRestore } from '@qualifio/ui-commons/dist/fonts/custom-fonts';
import { useTranslation } from 'react-i18next';
import { CurrencyDetails } from '../../libs/currency/components/detail/CurrencyDetails';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { useCurrencies } from '../../libs/currency/CurrenciesContext';
import { useFeatures } from '../../libs/features/FeaturesContext';

export function CurrenciesIndexPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const {
    currencies,
    selectedCurrency,
    setSelectedCurrency,
    createCurrency,
    archiveCurrency,
    deleteUnsavedCurrency,
    deleteUnsavedCurrencies,
    restoreCurrency,
  } = useCurrencies();
  const [searchField, setSearchField] = useState('');
  const [filters, setFilters] = useState<(string | number)[]>(['available']);
  const { features } = useFeatures();
  const {
    remove: currencyRemove,
    restore: currencyRestore,
    write: currencyWrite,
  } = features[Feature.CURRENCY];

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('currencies.sidebar.search')}
      onAdd={createCurrency}
      disableAdd={!currencyWrite}
      onAddTooltip={currencyWrite ? '' : t('shared.action.permission.required')}
      onChange={e => setSearchField(e.target.value)}
      withFilters={{
        filters: [
          {
            type: 'dropDown',
            name: t('master-detail.filters.availability'),
            options: [
              {
                value: 'available',
                label: t('master-detail.filters.available'),
              },
              { value: 'archived', label: t('master-detail.filters.archived') },
            ],
            selectedOptions: filters,
            onChange: (value: (string | number)[]) => {
              setFilters(value);
            },
          },
        ],
      }}
    />
  );

  const templateRow = (props: { data: CurrencyAttributes }) => {
    const { data: currency } = props;

    const options = currency.createdAt
      ? [
          currency.deletedAt
            ? {
                node: <p>{t('master-detail.actions.restore')}</p>,
                startIcon: <FontAwesomeIcon fixedWidth={true} icon={facRestore} />,
                callback: () => restoreCurrency(currency.id),
                disabled: !currencyRestore,
                tooltip: currencyRestore ? '' : t('shared.action.permission.required'),
              }
            : {
                node: <p>{t('master-detail.actions.archive')}</p>,
                startIcon: <FontAwesomeIcon fixedWidth={true} icon={faArchive} />,
                callback: () => {
                  archiveCurrency(currency.id);
                  setSelectedCurrency(undefined);
                },
                disabled: !currencyRemove,
                tooltip: currencyRemove ? '' : t('shared.action.permission.required'),
              },
        ]
      : [
          {
            node: <p>{t('generic.actions.hard-delete')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
            callback: () => deleteUnsavedCurrency(currency.id),
            disabled: !currencyRemove,
            tooltip: currencyRemove ? '' : t('shared.action.permission.required'),
          },
        ];

    return (
      <QlfSimpleDetail
        state={currency.id === selectedCurrency?.id ? 'active' : 'default'}
        options={options}
        callback={() => setSelectedCurrency(props.data)}
        content={getLocalizedProperty(currency, 'name', i18n.language)}
        image={getLocalizedProperty(currency, 'image', i18n.language)}
      />
    );
  };

  const newResults = currencies
    .filter(
      currency =>
        !searchField ||
        getLocalizedProperty(currency, 'name', i18n.language)?.includes(searchField),
    )
    .filter(
      currency =>
        (filters.includes('available') && !currency.deletedAt) ||
        (filters.includes('archived') && currency.deletedAt),
    );
  if (!!selectedCurrency && !newResults.includes(selectedCurrency)) {
    setSelectedCurrency(undefined);
  }

  useEffect(() => {
    return () => {
      deleteUnsavedCurrencies();
    };
  }, []);

  return (
    <QlfMasterDetailContainer>
      <QlfMasterDetail
        templateHeader={templateHeader}
        templateRow={templateRow}
        templateDetail={CurrencyDetails}
        items={newResults}
      />
    </QlfMasterDetailContainer>
  );
}
